import React, { useState } from 'react';
import { Loader2, Info, Play } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface AudioPlayerProps {
  attachment: {
    id: string;
    name?: string;
    type: string;
    url?: string;
  };
  messageId: string;
}

/**
 * AudioPlayer component for Facebook Messenger audio attachments
 *
 * Fetches audio URL on-demand using message_id approach (Facebook Graph API)
 */
export function AudioPlayer({ attachment, messageId }: AudioPlayerProps) {
  const [audioUrl, setAudioUrl] = useState<string | null>(attachment.url || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAudioUrl = async () => {
    if (loading || audioUrl) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.functions.invoke('get-message-attachment', {
        body: {
          message_id: messageId,
          attachment_id: attachment.id,
        },
      });

      if (fetchError) throw fetchError;

      if (data?.url) {
        setAudioUrl(data.url);
        toast.success('Nagranie załadowane');
      } else {
        throw new Error('No URL returned');
      }
    } catch (err: any) {
      console.error('Failed to fetch audio URL:', err);
      setError(err.message || 'Nie udało się pobrać audio');
      toast.error('Nie udało się załadować nagrania');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-black/20 rounded-lg p-3">
        <div className="flex items-center space-x-2 text-gray-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          <span>Pobieranie nagrania z Facebook...</span>
        </div>
      </div>
    );
  }

  // Show player if we have URL
  if (audioUrl) {
    return (
      <div className="bg-black/20 rounded-lg p-2">
        <audio controls className="w-full">
          <source src={audioUrl} type={attachment.type} />
          Twoja przeglądarka nie obsługuje audio.
        </audio>
        {attachment.name && (
          <p className="text-xs opacity-70 mt-1">{attachment.name}</p>
        )}
      </div>
    );
  }

  // Default: show info with play button
  return (
    <div className="bg-black/20 rounded-lg p-3">
      <div className="flex items-center space-x-2 text-gray-400 text-sm">
        <Info className="w-4 h-4 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium">Wiadomość głosowa</p>
          {attachment.name && (
            <p className="text-xs opacity-70">{attachment.name}</p>
          )}
          {error ? (
            <p className="text-xs text-red-400 mt-1">
              {error}
            </p>
          ) : (
            <p className="text-xs opacity-70 mt-1">
              Kliknij aby załadować i odtworzyć
            </p>
          )}
        </div>
        <button
          onClick={fetchAudioUrl}
          disabled={loading}
          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-xs flex items-center space-x-1 transition-colors"
        >
          <Play className="w-3 h-3" />
          <span>Odtwórz</span>
        </button>
      </div>
    </div>
  );
}
